import { forwardRef, useMemo } from 'react';

import {
  Button,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerProps,
  useDrawerInstance,
  Typography,
  Stack,
} from '@cast/design-system';
import { useSearchContext } from '@cast/shared-search-ui';
import { Cluster } from '@cast/types';

import { AgentStatusFilter } from '../../components/filters/AgentStatusFilter';
import { ManagedFilter } from '../../components/filters/ManagedFilter';
import { ProjectFilter } from '../../components/filters/ProjectFilter';
import { CLUSTERS_DRAWER_FILTER_KEYS } from '../../constants/clustersFilters';

export const ClustersFiltersDrawer = forwardRef<HTMLDivElement, DrawerProps>(
  (drawerProps, ref) => {
    const { close } = useDrawerInstance();
    const searchCtx = useSearchContext<Cluster>();

    const hasDrawerFilters = useMemo(
      () =>
        CLUSTERS_DRAWER_FILTER_KEYS.some((key) => {
          const value = searchCtx?.values?.[key];

          if (value == null) {
            return false;
          }

          if (Array.isArray(value)) {
            return value.length > 0;
          }

          return true;
        }),
      [searchCtx?.values]
    );

    const handleClearFilters = () => {
      CLUSTERS_DRAWER_FILTER_KEYS.forEach((key) => {
        searchCtx?.clearValue?.(key);
      });
    };

    return (
      <Drawer
        ref={ref}
        size="small"
        testId="clusters-filters-drawer"
        {...drawerProps}
      >
        <DrawerHeader />
        <DrawerBody>
          <DrawerContent gap={24}>
            <Typography variant="h5">Filters</Typography>
            <Stack gap={16}>
              <ProjectFilter fullWidth />
              <AgentStatusFilter fullWidth />
              <ManagedFilter fullWidth />
            </Stack>
          </DrawerContent>
        </DrawerBody>
        <DrawerFooter>
          {hasDrawerFilters ? (
            <Button
              variant="ghost"
              size="md"
              testId="clusters-filters-drawer-clear"
              onClick={handleClearFilters}
            >
              Clear filters
            </Button>
          ) : null}
          <Button
            variant="primary"
            size="md"
            testId="clusters-filters-drawer-done"
            onClick={() => close()}
          >
            Done
          </Button>
        </DrawerFooter>
      </Drawer>
    );
  }
);

ClustersFiltersDrawer.displayName = 'ClustersFiltersDrawer';
